import { useEffect, useState } from "react";
import api, { formatApiError } from "@/utils/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { fmtDate } from "@/components/governance/govUtils";

export const GovAudit = () => {
  const [rows, setRows] = useState([]);
  const [prefix, setPrefix] = useState("gov.");
  const [verify, setVerify] = useState(null);
  const [access, setAccess] = useState([]);
  useEffect(() => { api.get(`/governance/audit?prefix=${encodeURIComponent(prefix)}`).then(({ data }) => setRows(data)); }, [prefix]);
  useEffect(() => { api.get("/governance/access-review").then(({ data }) => setAccess(data)); }, []);
  const runVerify = async () => { const { data } = await api.get("/governance/audit/verify"); setVerify(data); };

  return (
    <div className="space-y-5" data-testid="gov-audit">
      <div className="bg-white border border-border rounded-md p-4 space-y-2">
        <div className="flex items-center justify-between"><h3 className="font-heading font-semibold text-sm">Audit log integrity</h3>
          <Button size="sm" variant="outline" onClick={runVerify} data-testid="gov-audit-verify-btn">Verify now</Button></div>
        <p className="text-xs text-muted-foreground">Each new record is numbered and sealed with a secret key. Verification detects edited records and missing numbers. Records from before {fmtDate("2026-10-22")} are unsealed.</p>
        {verify && (
          <p className={`flex items-center gap-1.5 text-sm font-medium ${verify.ok ? "text-emerald-700" : "text-red-700"}`} data-testid="gov-audit-verify-result">
            {verify.ok ? <ShieldCheck size={15} /> : <ShieldAlert size={15} />}
            {verify.ok ? `Intact: ${verify.checked} sealed records checked, no edits or gaps.` : `${verify.tampered_count} altered record(s), ${verify.gap_count} gap(s) found: preserve evidence and open an incident.`}
            <span className="text-xs font-normal text-muted-foreground"> · {verify.unsealed_legacy_records} legacy unsealed</span>
          </p>
        )}
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2"><h3 className="font-heading font-semibold text-sm">Events</h3>
          <select value={prefix} onChange={(e) => setPrefix(e.target.value)} className="h-8 rounded-md border border-border bg-white px-2 text-xs" data-testid="gov-audit-filter">
            <option value="gov.">Governance actions</option><option value="member.">Member changes</option><option value="mfa.">MFA</option><option value="login.">Logins</option><option value="">All</option></select></div>
        <div className="bg-white border border-border rounded-md max-h-[420px] overflow-y-auto">
          <table className="w-full text-xs"><tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid="gov-audit-row">
                <td className="px-3 py-1.5 whitespace-nowrap text-muted-foreground">{new Date(r.at).toLocaleString("en-GB")}</td>
                <td className="px-3 py-1.5 font-mono">{r.event}</td><td className="px-3 py-1.5">{r.actor_email}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{r.target_email || r.detail}</td>
                <td className="px-3 py-1.5">{r.seq ? <span className="text-emerald-700">#{r.seq}</span> : <span className="text-slate-400">legacy</span>}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      </div>
      <div className="space-y-2" data-testid="gov-access-review">
        <h3 className="font-heading font-semibold text-sm">Access review</h3>
        <p className="text-xs text-muted-foreground">Check each account still needs its access. Remove leavers in Admin Dashboard → Team Members. Record the review as evidence in Controls.</p>
        <div className="bg-white border border-border rounded-md max-h-[360px] overflow-y-auto">
          <table className="w-full text-xs"><tbody>
            {access.map((u) => (
              <tr key={u.id} className="border-b last:border-0">
                <td className="px-3 py-1.5">{u.name || "—"}</td><td className="px-3 py-1.5">{u.email}</td>
                <td className="px-3 py-1.5"><Badge variant="outline" className="text-[10px]">{u.role}</Badge></td>
                <td className="px-3 py-1.5">{u.mfa_enabled ? <span className="text-emerald-700">MFA on</span> : <span className={["admin", "superadmin"].includes(u.role) ? "text-red-600" : "text-slate-400"}>MFA off</span>}</td>
                <td className="px-3 py-1.5 text-muted-foreground">Last login {fmtDate(u.last_login_at)}</td>
                <td className="px-3 py-1.5 text-muted-foreground">{u.status === "archived" ? "Archived" : ""}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
      </div>
    </div>
  );
};

const RET = [["audit_log_days", "Security audit log (days)"], ["email_logs_days", "Email logs (days)"], ["chat_history_days", "AI assistant chats (days)"],
  ["auth_artefacts_days", "Used reset tokens / old lockouts (days)"], ["learner_inactive_review_years", "Flag inactive learners for review after (years)"]];

export const GovSettings = () => {
  const [r, setR] = useState(null);
  const [last, setLast] = useState(null);
  const [providers, setProviders] = useState([]);
  useEffect(() => { api.get("/governance/settings").then(({ data }) => { setR(data.retention); setProviders(data.provider_emails); }); }, []);
  const save = async () => { try { await api.put("/governance/settings/retention", r); toast.success("Retention schedule saved"); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const run = async () => {
    if (!window.confirm("Run the retention purge now? Records older than these periods will be permanently deleted.")) return;
    try { const { data } = await api.post("/governance/retention/run"); setLast(data); toast.success("Retention purge complete"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Purge failed"); }
  };
  if (!r) return null;
  return (
    <div className="space-y-4 max-w-2xl" data-testid="gov-settings">
      <div className="bg-white border border-border rounded-md p-4 space-y-3">
        <h3 className="font-heading font-semibold text-sm">Retention schedule</h3>
        <p className="text-xs text-muted-foreground">Runs automatically once a day. Learner records are never deleted automatically, only counted for review.</p>
        <div className="grid md:grid-cols-2 gap-3">
          {RET.map(([k, l]) => <div key={k}><Label className="text-xs">{l}</Label><Input type="number" value={r[k]} onChange={(e) => setR({ ...r, [k]: Number(e.target.value) })} data-testid={`gov-ret-${k}`} /></div>)}
        </div>
        <div className="flex gap-2"><Button onClick={save} data-testid="gov-ret-save">Save</Button><Button variant="outline" onClick={run} data-testid="gov-ret-run">Run purge now</Button></div>
        {last && <pre className="text-[11px] bg-slate-50 rounded p-2" data-testid="gov-ret-result">{JSON.stringify(last, null, 2)}</pre>}
      </div>
      <div className="bg-white border border-border rounded-md p-4 text-xs text-slate-600">
        <p className="font-semibold text-slate-800 mb-1">Provider access</p>
        <p>Governance Centre access is limited to these provider accounts (set by the GOVERNANCE_PROVIDER_EMAILS setting): {providers.join(", ")}</p>
      </div>
    </div>
  );
};
